import {
  setHours,
  setMinutes,
  setSeconds,
  parseJSON,
  addMinutes,
  endOfDay,
  startOfDay,
  eachDayOfInterval,
  parseISO,
  format,
  differenceInMinutes,
  isEqual,
  isSameDay,
  addDays,
  isValid,
  parse
} from "date-fns";
import { getCurrentCountryConfig } from "@/countries";

export const getDateFormat = (): string => getCurrentCountryConfig().dateFormat;
export const getDateFormatRegex = (): RegExp => new RegExp(getCurrentCountryConfig().dateFormatRegex);

/** Formats a date using the current market's date format (e.g. dd.MM.yyyy for NO). */
export const formatLocaleDate = (date: Date | string | null | undefined): string =>
  date ? format(dateFromApi(date), getDateFormat()) : "";

/**
 * School year label derived from a date, e.g. 2025/2026. Assumes a school year that
 * starts on 1 August (month index 7) — the convention for the Nordic school year.
 */
export const schoolYearLabel = (date: Date | string | null | undefined): string => {
  if (!date) return "";
  const d = dateFromApi(date);
  const startYear = d.getMonth() >= 7 ? d.getFullYear() : d.getFullYear() - 1;
  return `${startYear}/${startYear + 1}`;
};

export enum dayInWeek {
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAYS = 6,
  SUNDAY = 0
}

// tslint:disable-next-line: typedef
export const SetHours = (dato: Date | string, Hour: string): Date => {
  let resultdate: Date = parseJSON(dato);
  const s: string[] = Hour.split(":");
  if (s.length > 0) {
    resultdate = setHours(resultdate, parseInt(s[0], 0));
    if (s.length > 1) {
      resultdate = setMinutes(resultdate, parseInt(s[1], 0));
      if (s.length > 2) {
        resultdate = setSeconds(resultdate, parseInt(s[2], 0));
      } else {
        resultdate = setSeconds(resultdate, 0);
      }
    } else {
      resultdate = setMinutes(resultdate, 0);
      resultdate = setSeconds(resultdate, 0);
    }
  } else {
    resultdate = setHours(resultdate, 0);
    resultdate = setMinutes(resultdate, 0);
    resultdate = setSeconds(resultdate, 0);
  }
  return resultdate;
};

export const dateFromApi = (date: Date | string): Date => {
  if (!date) {
    // what to do when passed an undefined date
    throw new Error(
      "Passed value was undefined when it should have been either a date object or a string."
    );
  }

  if (date instanceof Date) {
    return date;
  }
  return parseISO(date as string);
};

export const convertDateToUtc = (date): Date => {
  const offset: number = 0 - date.getTimezoneOffset();
  return addMinutes(date, offset);
};

export const objectToDate = (obj): Date => {
  if ((typeof obj as any) === "string") {
    return parseISO(obj as any);
  } else {
    return obj as Date;
  }
};
export const convertDateOnlyToUtc = (date: Date) => {
  return format(date, "yyyy-MM-dd");
};

export const convertDateTimeToUtc = (date) => {
  return format(date, "yyyy-MM-dd'T'HH:mm");
};

export const timeFormatToMinutets = (minutes: string): number => {
  const timeRegex = /(0[0-9]|1[0-9]|2[0-3]|[0-9]):([0-5][0-9])/;
  if (minutes.length == 1) {
    minutes = "0" + minutes;
  }
  minutes = minutes.replace(":", "") + "0000";
  minutes = minutes.substr(0, 2) + ":" + minutes.substr(2, 2);
  const res = minutes.match(timeRegex);
  if (res) {
    return +res[2] + +res[1] * 60;
  } else {
    return 0;
  }
};

export const dateToMinuts = (date: Date): number => {
  const startday = startOfDay(date);
  return differenceInMinutes(date, startday);
};

export const minutsToHoursShort = (totalMinutes: number): string => {
  if (!totalMinutes) {
    return "0";
  }
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);

  const returnstr = `${hours}:${minutes.toString().padStart(2, "0")}`;
  return returnstr.replace(":00", "");
};

export const minutesToTimeFormat = (totalMinutes: number, sumMultiple = false): string => {
  if (!sumMultiple && (!totalMinutes || totalMinutes === 0 || totalMinutes === 1440)) {
    return "00:00";
  }
  const minutes = Math.floor(totalMinutes % 60);
  const hours = Math.floor(totalMinutes / 60);

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

export const timeToMinutes = (time: string, toTime = false): number => {
  if (toTime && time === "00:00") {
    return 1440; // 24:00
  }
  const hours: number = parseInt(time.split(":")[0]);
  const minutes: number = parseInt(time.split(":")[1]);
  return hours * 60 + minutes;
};

export const formatTimeString = (value: string): string => {
  if (!value) {
    return "";
  }
  const timeRegex = /(0[0-9]|1[0-9]|2[0-3]|[0-9]):([0-5][0-9])/;
  value = value.replaceAll("_", "").replace(":", "");
  if (!timeRegex.test(value) || value.length !== 5) {
    if (value.length == 1) {
      value = "0" + value;
    }
    const timeVal = (value + "0000").substr(0, 4);
    if (timeVal.length < 5 && isValid(parse(timeVal, "HHmm", new Date()))) {
      value = format(parse(timeVal, "HHmm", new Date()), "HH:mm");
    }
  }
  return value;
};

export const isTimeValid = (value: string): boolean => {
  if (!value || value.length == 0) {
    return true;
  }
  return isValid(parse(value, "HH:mm", new Date()));
};

export const getDateOfISOWeek = (w: number, y: number): Date => {
  const simple = new Date(y, 0, 1 + (w - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  return ISOweekStart;
};

/**
 * Iperiode functions
 */
export interface IPeriode {
  fromDate: Date;
  toDate: Date;
  Objects?: any[];
}

export const newTimeperiod = (): IPeriode => {
  return {
    fromDate: new Date(),
    toDate: new Date()
  };
};

export const newSearchSpace = (period1: IPeriode, period2: IPeriode): IPeriode => {
  const maxSearch = endOfDay(getMaxTo(period1, period2));
  const lowSearch = startOfDay(getLowestFrom(period1, period2));

  return {
    fromDate: lowSearch,
    toDate: maxSearch
  };
};

export const areTimeperiodsOverlapping = (period1: IPeriode, period2: IPeriode) => {
  return period1.fromDate < period2.toDate && period2.fromDate < period1.toDate;
};

export const getMaxTo = (period1: IPeriode, period2: IPeriode): Date => {
  return period1.toDate > period2.toDate ? period1.toDate : period2.toDate;
};

export const getLowestFrom = (period1: IPeriode, period2: IPeriode): Date => {
  return period1.fromDate < period2.fromDate ? period1.fromDate : period2.fromDate;
};

export const isCompletelyWrapped = (bigPeriod: IPeriode, smallPeriod: IPeriode): boolean => {
  return smallPeriod.fromDate >= bigPeriod.fromDate && smallPeriod.toDate <= bigPeriod.toDate;
};

export const overlapDay = (period: IPeriode, date: Date): boolean => {
  return period.fromDate <= date && period.toDate >= date;
};

export const getDatesBetween = (period: IPeriode): Date[] => {
  return eachDayOfInterval({
    start: period.fromDate,
    end: period.toDate
  });
};

export const getIntersectionDatetime = (
  periode1: IPeriode,
  periode2: IPeriode
): IPeriode | undefined => {
  let objects: any[] | undefined = [...(periode1.Objects || []), ...(periode2.Objects || [])];
  if (objects.length === 0) {
    objects = undefined;
  }
  if (periode1.fromDate < periode2.fromDate && periode1.toDate > periode2.toDate) {
    // 1:   #########################
    // 2:        #############
    return { fromDate: periode2.fromDate, toDate: periode2.toDate, Objects: objects } as IPeriode;
  } else if (periode2.fromDate < periode1.fromDate && periode2.toDate > periode1.toDate) {
    // 1:        #############
    // 2:   #########################
    return { fromDate: periode1.fromDate, toDate: periode1.toDate, Objects: objects } as IPeriode;
  } else if (
    periode1.fromDate > periode2.fromDate &&
    periode1.toDate > periode2.toDate &&
    periode1.fromDate < periode2.toDate &&
    periode1.toDate > periode2.fromDate
  ) {
    // 1:              #############
    // 2:        #############
    return { fromDate: periode1.fromDate, toDate: periode2.toDate, Objects: objects } as IPeriode;
  } else if (
    periode2.fromDate > periode1.fromDate &&
    periode2.toDate > periode1.toDate &&
    periode2.fromDate < periode1.toDate &&
    periode2.toDate > periode1.fromDate
  ) {
    // 1:        #############
    // 2:              #############
    return {
      fromDate: periode2.fromDate,
      toDate: periode1.toDate,
      Objects: objects
    } as IPeriode;
  } else if (isEqual(periode1.fromDate, periode2.fromDate) || isEqual(periode1.toDate, periode2.toDate)) {
    // 1: #####
    // 2: ##
    // or
    // 1: #####
    // 2:    ##
    // or
    // 1: #####
    // 2: #####
    return {
      fromDate: periode1.fromDate > periode2.fromDate ? periode1.fromDate : periode2.fromDate,
      toDate: periode1.toDate < periode2.toDate ? periode1.toDate : periode2.toDate,
      Objects: objects
    };
  }
  return undefined;
};

export const mergePeriods = (periods: IPeriode[]): IPeriode[] => {
  const sortedPeriods = [...periods].sort((a, b) => {
    if (a.fromDate > b.fromDate) {
      return 1;
    }
    if (a.fromDate < b.fromDate) {
      return -1;
    }

    return 0;
  });

  const output: IPeriode[] = [];

  let currentlyMerging: IPeriode | null = null;

  const periodsLength = sortedPeriods.length;
  for (let i = 0; i <= periodsLength; i++) {
    if (i === periodsLength) {
      // we have processed the last element in the list, push the current merging onto the output stack
      if (currentlyMerging) {
        output.push(currentlyMerging);
      }
      return output;
    }

    const current = sortedPeriods[i];

    if (currentlyMerging === null) {
      currentlyMerging = current; // we are the very first one, skipping
      continue;
    }

    const overlap =
      currentlyMerging.fromDate < current.toDate && current.fromDate < currentlyMerging.toDate; // if they overlap
    const dayDiff = isSameDay(addDays(currentlyMerging.toDate, 1), current.fromDate); // if they are only seperated by one day

    if (overlap || dayDiff) {
      if (!(current.toDate <= currentlyMerging.toDate)) {
        // if current.to is before currentlyMergin.to, the period can be considered already merged
        currentlyMerging.toDate = current.toDate;
      }
    } else {
      output.push(currentlyMerging);
      currentlyMerging = current;
    }
  }

  return output;
};
